import { findEmployeeByRole } from "./roleHelpers";

export const COMPLAINT_STATUS = {
  PENDING: "Pending",
  IN_PROGRESS: "In Progress",
  RESOLVED: "Resolved",
  REJECTED: "Rejected",
};

export const COMPLAINT_KIND = {
  COMPLAINT: "COMPLAINT",
  INQUIRY: "INQUIRY",
};

export function getComplaintKindLabel(kind) {
  if (kind === COMPLAINT_KIND.INQUIRY) return "استفسار";
  return "شكوى";
}

const ROLE_LABELS = {
  manager: "المدير المباشر",
  "regional-manager": "مدير المنطقة",
  "department-manager": "مدير الإدارة",
  hr: "الموارد البشرية",
  gm: "المدير العام",
};

const ALLOWED_RECEIVER_ROLES = {
  employee: ["manager", "regional-manager", "department-manager", "hr"],
  manager: ["regional-manager", "department-manager", "hr"],
  "regional-manager": ["department-manager", "hr"],
  "department-manager": ["hr"],
  hr: ["gm"],
};

function resolveComplaintSenderKey(senderRole, backendRoles = []) {
  if (backendRoles.includes("gm") || backendRoles.includes("admin")) {
    return null;
  }
  if (backendRoles.includes("hr")) return "hr";
  if (backendRoles.includes("department_manager")) return "department-manager";
  if (backendRoles.includes("regional_manager")) return "regional-manager";
  if (
    backendRoles.includes("manager") ||
    backendRoles.includes("branch_manager") ||
    backendRoles.includes("team_leader")
  ) {
    return "manager";
  }
  return senderRole;
}

export function getStatusLabel(status) {
  const labels = {
    [COMPLAINT_STATUS.PENDING]: "⏳ قيد الانتظار",
    [COMPLAINT_STATUS.IN_PROGRESS]: "🔄 قيد المعالجة",
    [COMPLAINT_STATUS.RESOLVED]: "✅ تم الحل",
    [COMPLAINT_STATUS.REJECTED]: "❌ مرفوض",
  };
  return labels[status] || status;
}

export const COMPLAINT_ACTION_LABELS = {
  CREATED: "تقديم",
  IN_PROGRESS: "قيد المعالجة",
  RESOLVED: "تم الحل",
  REJECTED: "مرفوض",
  TRANSFERRED: "تحويل",
};

export function getComplaintActionLabel(action) {
  return COMPLAINT_ACTION_LABELS[action] || action;
}

export function getStatusBadgeClass(status) {
  if (status === COMPLAINT_STATUS.RESOLVED) return "approved";
  if (status === COMPLAINT_STATUS.REJECTED) return "rejected";
  return "pending";
}

export function resolveUserName(id, getEmployeeById) {
  const emp = getEmployeeById?.(id);
  return emp?.name || `مستخدم #${id}`;
}

function buildRecipient(id, role, getEmployeeById, fallbackName) {
  if (!id) return null;
  return {
    id,
    name: getEmployeeById?.(id)?.name || fallbackName || `مستخدم #${id}`,
    role,
    label: ROLE_LABELS[role] || role,
  };
}

/**
 * يُرجع قائمة المستلمين المتاحين ضمن السلسلة الإدارية.
 */
export function getAvailableComplaintRecipients(
  user,
  getEmployeeById,
  allEmployees = [],
) {
  if (!user || user.role === "gm") return [];

  const senderKey =
    resolveComplaintSenderKey(user.role, user.backendRoles || []) || user.role;
  const allowedRoles = ALLOWED_RECEIVER_ROLES[senderKey] || [];
  const record =
    getEmployeeById(user.employeeId) ||
    getEmployeeById(user.id) ||
    allEmployees.find((e) => e.userId === user.id);
  const hrEmployee = findEmployeeByRole(allEmployees, "hr");
  const gmEmployee = findEmployeeByRole(allEmployees, "gm");
  const recipients = [];
  const seen = new Set();

  function addRecipient(entry) {
    if (!entry || seen.has(entry.id)) return;
    if (!allowedRoles.includes(entry.role)) return;
    seen.add(entry.id);
    recipients.push(entry);
  }

  if (senderKey === "hr") {
    if (gmEmployee?.id) {
      addRecipient(
        buildRecipient(gmEmployee.id, "gm", getEmployeeById),
      );
    }
    return recipients;
  }

  // Walk manager chain locally when directory data is available.
  let currentId = record?.managerId;
  const visited = new Set([record?.id, user.employeeId, user.id].filter(Boolean));
  let hops = 0;
  while (currentId && hops < 12) {
    if (visited.has(currentId)) break;
    visited.add(currentId);
    const superior = getEmployeeById(currentId);
    if (!superior) break;

    const memberLevel = superior.memberLevel || superior.role;
    let role = "manager";
    if (memberLevel === "regional-manager" || superior.roleSlugs?.includes("regional_manager")) {
      role = "regional-manager";
    } else if (
      memberLevel === "department-manager" ||
      superior.roleSlugs?.includes("department_manager")
    ) {
      role = "department-manager";
    } else if (memberLevel === "hr" || superior.roleSlugs?.includes("hr")) {
      role = "hr";
    } else if (memberLevel === "gm" || superior.roleSlugs?.includes("gm")) {
      role = "gm";
    }

    addRecipient(
      buildRecipient(superior.id, role, getEmployeeById, superior.name),
    );
    currentId = superior.managerId;
    hops += 1;
  }

  if (allowedRoles.includes("regional-manager")) {
    const regionalManager = allEmployees.find(
      (e) =>
        e.memberLevel === "regional-manager" ||
        e.roleSlugs?.includes("regional_manager"),
    );
    if (regionalManager?.id) {
      addRecipient(
        buildRecipient(
          regionalManager.id,
          "regional-manager",
          getEmployeeById,
          regionalManager.name,
        ),
      );
    }
  }

  if (allowedRoles.includes("department-manager") && record?.departmentManagerId) {
    addRecipient(
      buildRecipient(
        record.departmentManagerId,
        "department-manager",
        getEmployeeById,
      ),
    );
  }

  if (allowedRoles.includes("hr") && hrEmployee?.id) {
    addRecipient(buildRecipient(hrEmployee.id, "hr", getEmployeeById));
  }

  if (allowedRoles.includes("gm") && gmEmployee?.id) {
    addRecipient(buildRecipient(gmEmployee.id, "gm", getEmployeeById));
  }

  return recipients;
}

export function isValidComplaintRecipient(
  senderRole,
  receiverRole,
  backendRoles = [],
) {
  const key = resolveComplaintSenderKey(senderRole, backendRoles) || senderRole;
  return (ALLOWED_RECEIVER_ROLES[key] || []).includes(receiverRole);
}

export function canUserSendComplaint(role) {
  return role !== "gm";
}

export function getRoleLabel(role) {
  return ROLE_LABELS[role] || role;
}

/** Map frontend user roles to backend workflow / auth role slugs. */
const VIEWER_ROLE_TO_APPROVER_SLUGS = {
  manager: ["manager", "team_leader", "branch_manager"],
  "regional-manager": ["regional_manager"],
  "department-manager": ["department_manager"],
  hr: ["hr", "hr_staff"],
  "hr-staff": ["hr", "hr_staff"],
  gm: ["gm"],
};

function normalizeComplaintRoleSlug(role) {
  if (!role) return null;
  if (role === "department-manager") return "department_manager";
  if (role === "regional-manager") return "regional_manager";
  return role;
}

function viewerMatchesRoleSlug(userRole, roleSlug) {
  if (!userRole || !roleSlug) return false;
  const matchingSlugs = VIEWER_ROLE_TO_APPROVER_SLUGS[userRole] || [];
  return matchingSlugs.includes(normalizeComplaintRoleSlug(roleSlug));
}

function viewerMatchesViaBackendRoles(backendRoles, roleSlug) {
  if (!backendRoles?.length || !roleSlug) return false;
  const normalized = normalizeComplaintRoleSlug(roleSlug);
  if (backendRoles.includes(normalized)) return true;
  if (
    normalized === "hr" &&
    (backendRoles.includes("hr") || backendRoles.includes("hr_staff"))
  ) {
    return true;
  }
  if (
    normalized === "manager" &&
    (backendRoles.includes("manager") ||
      backendRoles.includes("team_leader") ||
      backendRoles.includes("branch_manager"))
  ) {
    return true;
  }
  return false;
}

/** True when the complaint is currently waiting on this viewer's workflow step. */
export function isComplaintAwaitingViewer(
  complaint,
  userRole,
  backendRoles = [],
  userId,
) {
  if (!complaint || !userRole) return false;

  if (complaint.assignedUserId) {
    return userId != null && String(complaint.assignedUserId) === String(userId);
  }

  const roleSlug = complaint.currentApproverRole || complaint.receiverRole;

  if (backendRoles?.length) {
    return viewerMatchesViaBackendRoles(backendRoles, roleSlug);
  }

  return viewerMatchesRoleSlug(userRole, roleSlug);
}

function isClosedComplaint(complaint) {
  return (
    complaint?.status === COMPLAINT_STATUS.RESOLVED ||
    complaint?.status === COMPLAINT_STATUS.REJECTED
  );
}

/** Open complaint or inquiry currently waiting on this viewer. */
export function isComplaintNeedingViewerAction(
  complaint,
  userRole,
  backendRoles = [],
  userId,
) {
  if (isClosedComplaint(complaint)) return false;

  // GM inbox lists every complaint. The badge counts only the GM step.
  if (userRole === "gm") {
    if (complaint?.assignedUserId) {
      return (
        userId != null && String(complaint.assignedUserId) === String(userId)
      );
    }
    const roleSlug = complaint?.currentApproverRole || complaint?.receiverRole;
    return normalizeComplaintRoleSlug(roleSlug) === "gm";
  }

  return isComplaintAwaitingViewer(complaint, userRole, backendRoles, userId);
}

export function countComplaintsNeedingViewerAction(complaints, user) {
  return (complaints || []).filter((complaint) =>
    isComplaintNeedingViewerAction(
      complaint,
      user?.role,
      user?.backendRoles || [],
      user?.id,
    ),
  ).length;
}

/**
 * Whether a complaint belongs in this viewer's inbox:
 * - currently awaiting their step, or
 * - originally directed to their role (history), or
 * - legacy receiverId match
 */
export function isComplaintForViewerRole(
  complaint,
  userRole,
  userId,
  backendRoles = [],
) {
  if (!complaint) return false;
  if (userRole === "hr" || userRole === "hr-staff" || userRole === "gm") return true;

  if (isComplaintAwaitingViewer(complaint, userRole, backendRoles, userId)) return true;

  if (backendRoles?.length) {
    if (viewerMatchesViaBackendRoles(backendRoles, complaint.targetRole)) {
      return true;
    }
  } else if (viewerMatchesRoleSlug(userRole, complaint.targetRole)) {
    return true;
  }

  if (
    complaint.receiverId != null &&
    userId != null &&
    String(complaint.receiverId) === String(userId)
  ) {
    return true;
  }

  return false;
}

export function getComplaintsForRole(
  complaints,
  userRole,
  userId,
  backendRoles = [],
) {
  if (userRole === "hr" || userRole === "hr-staff" || userRole === "gm" || userRole === "owner") return [...complaints];
  return complaints.filter((c) =>
    isComplaintForViewerRole(c, userRole, userId, backendRoles),
  );
}

export function buildHrAwarenessNotification(
  complaint,
  createNotification,
  NOTIFICATION_TYPES,
  hrEmployeeId,
) {
  if (!hrEmployeeId) return null;

  const kindLabel = getComplaintKindLabel(complaint.kind);
  const isInquiry = complaint.kind === COMPLAINT_KIND.INQUIRY;

  return createNotification({
    recipientId: hrEmployeeId,
    recipientRole: "hr",
    type: NOTIFICATION_TYPES.COMPLAINT_NEW,
    title: isInquiry
      ? "📋 استفسار جديد في النظام"
      : "📋 شكوى جديدة في النظام",
    message: `قدّم ${complaint.senderName} ${kindLabel} "${complaint.title}" إلى ${complaint.receiverName} (${getRoleLabel(complaint.receiverRole)})`,
    relatedRequestId: complaint.id,
    senderName: complaint.senderName,
    senderRole: complaint.senderRole,
    requestType: "complaint",
    requestStatus: complaint.status,
  });
}

export function notifyHrOfNewComplaint(
  complaint,
  { createNotification, NOTIFICATION_TYPES, addNotificationToUser, hrEmployeeId },
) {
  if (!hrEmployeeId || complaint.receiverId === hrEmployeeId) return;

  const hrNotif = buildHrAwarenessNotification(
    complaint,
    createNotification,
    NOTIFICATION_TYPES,
    hrEmployeeId,
  );
  if (hrNotif) addNotificationToUser(hrEmployeeId, hrNotif);
}
